import React, { useEffect, useState } from "react";
import {
  collection,
  getDocs,
  updateDoc,
  doc,
  increment,
  getDoc,
  deleteDoc,
  getFirestore,
} from "firebase/firestore";
import RequestNotice from "../components/RequestNotice";
import { isFinalStatus, notifyRequestOutcome } from "../utils/requestNotify";

function ProjectRequestsPage() {
  const db = getFirestore(); // Initialize Firestore

  const [requests, setRequests] = useState([]);
  const [refundCoins, setRefundCoins] = useState(0);
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [notice, setNotice] = useState(null);

  useEffect(() => {
    const fetchRefundCoins = async () => {
      const adminDoc = await getDoc(doc(db, "adminPanel", "admin"));
      if (adminDoc.exists()) {
        setRefundCoins(adminDoc.data().projectTopicRequestCost || 0);
      } else {
        console.log("No such document!");
      }
    };

    const fetchRequests = async () => {
      const querySnapshot = await getDocs(
        collection(db, "projectTopicRequests"),
      );
      const requestsData = querySnapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));
      setRequests(requestsData);
    };

    fetchRefundCoins();
    fetchRequests();
  }, [db]);

  // Emails the user about the outcome and shows the admin whether it went out.
  const sendNotification = async (id, refundedCoins = 0) => {
    const result = await notifyRequestOutcome("project", id, refundedCoins);
    if (!result.ok) {
      setNotice({ type: "error", text: `Status saved, but the email was not sent: ${result.message}` });
      return;
    }
    setRequests((prev) => prev.map((item) => (item.id === id ? { ...item, notifiedStatus: item.status } : item)));
    setNotice({ type: "success", text: result.already ? "The user was already emailed about this." : "Status saved and the user was emailed." });
  };

  const handleStatusChange = async (id, newStatus, userId = null) => {
    try {
      const requestDoc = doc(db, "projectTopicRequests", id);
      await updateDoc(requestDoc, { status: newStatus });

      if (newStatus === "declined" && refundCoins > 0 && userId) {
        const userDoc = doc(db, "users", userId);
        await updateDoc(userDoc, { coins: increment(refundCoins) });
      }

      setRequests((prevRequests) =>
        prevRequests.map((request) =>
          request.id === id ? { ...request, status: newStatus } : request,
        ),
      );
      await sendNotification(id, newStatus === "declined" && refundCoins > 0 && userId ? refundCoins : 0);
    } catch (error) {
      console.error("Error updating request status:", error);
      setNotice({ type: "error", text: "Could not update the status. Please try again." });
    }
  };

  const handleDeleteRequest = async (id) => {
    if (!window.confirm("Delete this project/topic request?")) return;

    try {
      await deleteDoc(doc(db, "projectTopicRequests", id));
      setRequests((prevRequests) =>
        prevRequests.filter((request) => request.id !== id),
      );
    } catch (error) {
      console.error("Error deleting project request:", error);
    }
  };

  const statusCounts = {
    all: requests.length,
    pending: requests.filter((item) => item.status?.toLowerCase() === "pending")
      .length,
    completed: requests.filter(
      (item) => item.status?.toLowerCase() === "completed",
    ).length,
    declined: requests.filter(
      (item) => item.status?.toLowerCase() === "declined",
    ).length,
  };

  const filteredRequests = requests
    .filter((request) => {
      if (statusFilter === "all") return true;
      return (request.status || "").toLowerCase() === statusFilter;
    })
    .filter((request) => {
      const query = searchQuery.toLowerCase();
      return (
        request.email?.toLowerCase().includes(query) ||
        request.course?.toLowerCase().includes(query) ||
        request.status?.toLowerCase().includes(query) ||
        request.topicCount?.toString().includes(query)
      );
    });

  return (
    <div className="max-w-5xl mx-auto p-6 bg-white rounded-lg shadow-lg mt-8">
      <h1 className="text-2xl font-bold text-gray-800 mb-6 text-center">
        Project Requests
      </h1>
      <RequestNotice notice={notice} onDismiss={() => setNotice(null)} />
      {/* Search Input */}
      <div className="mb-4">
        <div className="mb-4 text-center text-sm text-gray-600">
          <span className="mr-3">Pending: {statusCounts.pending}</span>
          <span className="mr-3">Completed: {statusCounts.completed}</span>
          <span>Declined: {statusCounts.declined}</span>
        </div>
        <div className="flex flex-wrap gap-2 mb-3">
          {[
            { key: "all", label: `All (${statusCounts.all})` },
            { key: "pending", label: `Pending (${statusCounts.pending})` },
            {
              key: "completed",
              label: `Completed (${statusCounts.completed})`,
            },
            { key: "declined", label: `Declined (${statusCounts.declined})` },
          ].map((filter) => (
            <button
              key={filter.key}
              onClick={() => setStatusFilter(filter.key)}
              className={`px-3 py-2 rounded-md border transition duration-200 ${
                statusFilter === filter.key
                  ? "bg-purple-800 text-white border-purple-800"
                  : "bg-white text-gray-700 border-gray-300 hover:bg-gray-100"
              }`}
            >
              {filter.label}
            </button>
          ))}
        </div>
        <input
          type="text"
          placeholder="Search requests by email, course, status, or topic count"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="w-full px-4 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-purple-600"
        />
      </div>
      {filteredRequests.length === 0 ? (
        <p className="text-gray-600 text-center">No project requests found.</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full table-auto border-collapse">
            <thead>
              <tr className="bg-purple-800 text-white">
                <th className="px-4 py-3 text-left text-sm font-medium">
                  Email
                </th>
                <th className="px-4 py-3 text-left text-sm font-medium">
                  Course
                </th>
                <th className="px-4 py-3 text-left text-sm font-medium">
                  Topic Count
                </th>
                <th className="px-4 py-3 text-left text-sm font-medium">
                  Status
                </th>
                <th className="px-4 py-3 text-left text-sm font-medium">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody>
              {filteredRequests.map((request, index) => (
                <tr
                  key={request.id}
                  className={`${
                    index % 2 === 0 ? "bg-gray-50" : "bg-white"
                  } hover:bg-gray-100 transition duration-200`}
                >
                  <td className="px-4 py-3 text-sm text-gray-700">
                    {request.email}
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-700">
                    {request.course}
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-700">
                    {request.topicCount}
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-700 capitalize">
                    {request.status}
                  </td>
                  <td className="px-4 py-3 text-sm">
                    {request.status === "pending" ? (
                      <div className="flex flex-col sm:flex-row sm:space-x-2 gap-2">
                        <button
                          onClick={() =>
                            handleStatusChange(request.id, "completed")
                          }
                          className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 transition duration-200"
                        >
                          Mark as Completed
                        </button>
                        <button
                          onClick={() =>
                            handleStatusChange(
                              request.id,
                              "declined",
                              request.userId,
                            )
                          }
                          className="bg-red-500 text-white px-4 py-2 rounded-md hover:bg-red-600 transition duration-200"
                        >
                          Decline and Refund
                        </button>
                      </div>
                    ) : (
                      <div className="flex flex-col sm:flex-row sm:space-x-2 gap-2">
                        {isFinalStatus(request.status) && request.notifiedStatus !== request.status && (
                          <button
                            onClick={() => sendNotification(request.id)}
                            className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 transition duration-200"
                          >
                            Send email
                          </button>
                        )}
                        <button
                          onClick={() => handleDeleteRequest(request.id)}
                          className="bg-gray-700 text-white px-4 py-2 rounded-md hover:bg-gray-800 transition duration-200"
                        >
                          Delete
                        </button>
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

export default ProjectRequestsPage;
